import { computed, type ComputedRef } from 'vue'
import { useServices } from '~/api/models'
import type {
  ServiceCapabilities,
  ServiceDashboardCapabilities,
  ServiceReportCapabilities
} from '~/types/report-service'

export type ReportCapabilityKey =
  'reports.view' | 'reports.author' | 'reports.schedule' | 'reports.export' | 'dashboards.view' | 'dashboards.author'

export const REPORT_CAPABILITIES_FALLBACK: ServiceCapabilities = {
  reports: { view: true, author: false, schedule: true, export: true },
  dashboards: { view: false, author: false },
  packs: null
}

const CAPABILITY_READERS: Record<ReportCapabilityKey, (capabilities: ServiceCapabilities) => boolean> = {
  'reports.view': (capabilities) => capabilities.reports.view,
  'reports.author': (capabilities) => capabilities.reports.author,
  'reports.schedule': (capabilities) => capabilities.reports.schedule,
  'reports.export': (capabilities) => capabilities.reports.export,
  'dashboards.view': (capabilities) => capabilities.dashboards.view,
  'dashboards.author': (capabilities) => capabilities.dashboards.author
}

export const REPORT_CAPABILITY_KEYS = Object.keys(CAPABILITY_READERS) as readonly ReportCapabilityKey[]

const toFlag = (value: unknown, fallback: boolean): boolean => (typeof value === 'boolean' ? value : fallback)

const toPacks = (value: unknown): ServiceCapabilities['packs'] =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : null

export interface ReportCapabilitiesAnswer {
  reports?: Partial<ServiceReportCapabilities>
  dashboards?: Partial<ServiceDashboardCapabilities>
  packs?: unknown
}

export const resolveReportCapabilities = (answer?: ReportCapabilitiesAnswer | null): ServiceCapabilities => {
  const reports: Partial<ServiceReportCapabilities> = answer?.reports ?? {}
  const dashboards: Partial<ServiceDashboardCapabilities> = answer?.dashboards ?? {}

  return {
    reports: {
      view: toFlag(reports.view, REPORT_CAPABILITIES_FALLBACK.reports.view),
      author: toFlag(reports.author, REPORT_CAPABILITIES_FALLBACK.reports.author),
      schedule: toFlag(reports.schedule, REPORT_CAPABILITIES_FALLBACK.reports.schedule),
      export: toFlag(reports.export, REPORT_CAPABILITIES_FALLBACK.reports.export)
    },
    dashboards: {
      view: toFlag(dashboards.view, REPORT_CAPABILITIES_FALLBACK.dashboards.view),
      author: toFlag(dashboards.author, REPORT_CAPABILITIES_FALLBACK.dashboards.author)
    },
    packs: toPacks(answer?.packs)
  }
}

export const hasReportCapability = (capabilities: ServiceCapabilities, key: ReportCapabilityKey): boolean =>
  CAPABILITY_READERS[key]?.(capabilities) ?? false

export interface UseReportCapabilitiesReturn {
  capabilities: ComputedRef<ServiceCapabilities>
  can: (key: ReportCapabilityKey) => ComputedRef<boolean>
  canViewReports: ComputedRef<boolean>
  canAuthorReports: ComputedRef<boolean>
  canScheduleReports: ComputedRef<boolean>
  canExportReports: ComputedRef<boolean>
  canViewDashboards: ComputedRef<boolean>
  canAuthorDashboards: ComputedRef<boolean>
  isKnown: ComputedRef<boolean>
  isLoading: ComputedRef<boolean>
}

export const useReportCapabilities = (): UseReportCapabilitiesReturn => {
  const services = useServices()
  const query = services.reportService.useGetCapabilitiesQuery()

  const answered = computed<ServiceCapabilities | null>(() => query.data.value ?? null)

  const capabilities = computed<ServiceCapabilities>(() =>
    answered.value ? resolveReportCapabilities(answered.value) : REPORT_CAPABILITIES_FALLBACK
  )

  const can = (key: ReportCapabilityKey): ComputedRef<boolean> =>
    computed(() => hasReportCapability(capabilities.value, key))

  return {
    capabilities,
    can,
    canViewReports: can('reports.view'),
    canAuthorReports: can('reports.author'),
    canScheduleReports: can('reports.schedule'),
    canExportReports: can('reports.export'),
    canViewDashboards: can('dashboards.view'),
    canAuthorDashboards: can('dashboards.author'),
    isKnown: computed(() => answered.value !== null),
    isLoading: computed(() => query.isLoading.value)
  }
}
